import React from 'react';
import ReactDOM from 'react-dom';
import {PropTypes} from 'prop-types';
import moment from 'moment-timezone';
import numeral from 'numeral';
import {ErrorsBlockComponent} from '../../shared/errorsBlockComponent';
import {PageHeader} from '../../shared/PageHeader';
import {ObjectHasProperty, MakeApiCall, ToString, ParseStyles, NormalizeClassNameString, IsErrorsEmpty} from '../../shared/utils';
import {SimpleLineChart, NormalizeDataTable} from '../../shared/Charts';
import {RefreshButtonComponent} from '../../shared/refreshButtonComponent';
import {AssignObjectDataByMap, CloneObject} from '../../shared/123';
import {Checkbox} from '../../shared/checkbox';
import {APPLICATION_TYPE} from '../../app.defs';
import { DurationComponent } from '../../components/job/durationComponent';

import './styles.scss';

const DefaultChartOptions = {
    fontSize: 11,
    lineWidth: 1,
    pointsVisible: false,
    backgroundColor: 'transparent',
    legend: { position: 'none' },
    tooltip: { ignoreBounds: true, textStyle: { fontSize: 14 }},
    series: {
        0: { color: '#396caa' },
        1: { color: '#eb5757' },
    },
}

const JobRunsChartOptions = 
{
    ...CloneObject(DefaultChartOptions),
    ...{
        legend: { position: 'right', textStyle: { fontSize: 12 } },
        series: {
            0: { color: '#396caa' },
            1: { color: '#219653' },
            2: { color: '#eb5757' },
        }
    }
}

const UploadsChartOptions = 
{
    ...CloneObject(DefaultChartOptions),
    ...{
        legend: { position: 'right' },
    }
}

const BackupsChartOptions = CloneObject(UploadsChartOptions);

const DownloadsChartOptions = CloneObject(JobRunsChartOptions);

const RestoresChartOptions = CloneObject(JobRunsChartOptions);

const AgentsChartOptions =  
{
    ...CloneObject(DefaultChartOptions),
    ...{
        legend: { position: 'right' },
        series: null,
        height: 380,
        explorer: {
            keepInBounds: true,
            maxZoomOut: 1,
            zoomDelta: 1.25
        },
    }
}

const MailsChartOptions = CloneObject(UploadsChartOptions);


class SimpleTable extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);

        this.state = {scrollTop: 0};
        this._tableRef = React.createRef();
        this.updateHeaderPosition = this.updateHeaderPosition.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
    }
    
    updateHeaderPosition() {
        const self = this;
        const scrollTop = ReactDOM.findDOMNode(self._tableRef.current).scrollTop;
        if (self.state.scrollTop != scrollTop) {
            self.setState({scrollTop: scrollTop});
        }
    }

    handleScroll() {
        this.updateHeaderPosition();
    }

    render() {
        const self = this;

        const {tableData, className, style, timeZoneId, rowsFilter} = self.props;
        const data = {...{cols: [], rows: []}, ...tableData};
        
        const applyProps = (elementProps, props) => {
            const result = {...elementProps};

            if (props) {
                if (props.class) {
                    result.className = NormalizeClassNameString(ToString(props.class));
                }
                if (props.colSpan) {
                    result.colSpan = props.colSpan;
                }
                if (props.style) {
                    if (typeof props.style === 'string') {
                        result.style = ParseStyles(props.style);
                    }
                    else if (typeof props.style === 'object') {
                        result.style = props.style;
                    }
                }
                if (props.title) {
                    result.title = ToString(props.title);
                }
            }

            return result;
        }

        const formatValue = (v, colIndex) => {

            let result = ToString(v);

            const col = data.cols[colIndex];

            if (Boolean(col)) {
                switch (col.type) {
                    case 'datetime':
                        const dt = moment.utc(v);
                        if (dt.isValid()) {
                            result = dt.tz(timeZoneId).format('L LT');
                        }

                        break;

                    case 'date':
                        const d = moment.utc(v);
                        if (d.isValid()) {
                            result = d.tz(timeZoneId).format('L');
                        }

                        break;
                }
            }

            return result;
        }

        const filter = rowsFilter || (p => true);

        return(
            <table className={className} style={style} ref={self._tableRef} onScroll={self.handleScroll}>
                <thead style={{transform: `translateY(${self.state.scrollTop}px)`}}>
                    <tr>
                        {data.cols.map((col, index) => {
                            const colLabel = (col.label || '') + '';
                            const props = applyProps({key: index}, col.p);

                            return (<th {...props}>{colLabel}</th>)
                        })}
                    </tr>
                </thead>
                <tbody>
                    {data.rows.filter(filter).map((row, rindex) => {
                        const props = applyProps({key: rindex}, row.p);

                        return (
                            <tr {...props}>
                                {row.c.map((cell, cindex) => {
                                    const props = applyProps({key: cindex}, cell.p);

                                    return (<td {...props}>{cell.f || formatValue(cell.v, cindex)}</td>)
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        )
    }
}
SimpleTable.propTypes = {
    tableData: PropTypes.object,
    style: PropTypes.object,
    className: PropTypes.string,
    rowsFilter: PropTypes.func
}
SimpleTable.defaultProps = {
    tableData: null,
    style: null,
    className: 'table-striped-even w-auto d-block table-hover newstyle'
}

const PresetDataMap = {
    jobsRunStats: {
        filter: {
            ApplicationTypeId: '',
            JobTypeId: '',
        },
        isOpened: false,
        invisibleColumns: null,
    },
    accountsWithAgentsStats: {
        filter: {
            ApplicationTypeId: '',
        },
        isOpened: false,
        invisibleColumns: null,
    },
    activeAccountsStats: {
        filter: {
            ApplicationTypeId: '',
        },
        isOpened: false,
        invisibleColumns: null,
    },
    backupsStats: {
        filter: {
            ApplicationTypeId: '',
            ServerTypeId: '',
            BackupsBackupTypeId: '',
        },
        isOpened: false,
        invisibleColumns: null,
    },
    uploadsStats: {
        filter: {
            ApplicationTypeId: '',
            DestinationTypeId: '',
        },
        isOpened: false,
        invisibleColumns: null,
    },
    downloadsStats: {
        filter: {
            ApplicationTypeId: '',
            DestinationTypeId: '',
        },
        isOpened: false,
        invisibleColumns: null,
    },
    restoresStats: {
        filter: {
            ApplicationTypeId: '',
            ServerTypeId: '',
        },
        isOpened: false,
        invisibleColumns: null,
    },
    agentsByVersionStats: {
        filter: {
            ApplicationTypeId: APPLICATION_TYPE.SqlBakForWindows,
            HideInactive: true,
        },
        isOpened: false,
        invisibleColumns: null,
    },
    agentsByDestinationTypeStats: {
        filter: {
            ApplicationTypeId: '',
        },
        isOpened: false,
        invisibleColumns: null,
    },
    agentsByServerTypeStats: {
        filter: {
            ApplicationTypeId: '',
        },
        isOpened: false,
        invisibleColumns: null,
    },
    agentsByServicePlanStats: {
        filter: {
            ApplicationTypeId: '',
        },
        isOpened: false,
        invisibleColumns: null,
    },
    mailsStats: {
        filter: {
            MailTypeId: '',
        },
        isOpened: false,
        invisibleColumns: null,
    },
}

const DefaultDisplayOrders = [
    'jobsRunStats',
    'accountsWithAgentsStats',
    'activeAccountsStats',
    'backupsStats',
    'uploadsStats',
    'downloadsStats',
    'restoresStats',
    'agentsByVersionStats',
    'agentsByDestinationTypeStats',
    'agentsByServerTypeStats',
    'agentsByServicePlanStats',
    'mailsStats'
]

export class AdminDashboardComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);

        const state = {
            jobsRunStats: {
                filter: {
                    ApplicationTypeId: '',
                    JobTypeId: '',
                },
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
            accountsWithAgentsStats: {
                filter: {
                    ApplicationTypeId: '',
                },
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
            activeAccountsStats: {
                filter: {
                    ApplicationTypeId: '',
                },
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
            backupsStats: {
                filter: {
                    ApplicationTypeId: '',
                    ServerTypeId: '',
                    BackupsBackupTypeId: '',
                },
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
            uploadsStats: {
                filter: {
                    ApplicationTypeId: '',
                    DestinationTypeId: '',
                },
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
            downloadsStats: {
                filter: {
                    ApplicationTypeId: '',
                    DestinationTypeId: '',
                },
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
            restoresStats: {
                filter: {
                    ApplicationTypeId: '',
                    ServerTypeId: '',
                },
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
            agentsByVersionStats: {
                filter: {
                    ApplicationTypeId: APPLICATION_TYPE.SqlBakForWindows,
                    HideInactive: true,
                },
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
            agentsByDestinationTypeStats: {
                filter: {
                    ApplicationTypeId: '',
                },
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
            agentsByServerTypeStats: {
                filter: {
                    ApplicationTypeId: '',
                },
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
            agentsByServicePlanStats: {
                filter: {
                    ApplicationTypeId: '',
                },
                invisibleColumns: [1],
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
            mailsStats: {
                filter: {
                    MailTypeId: '',
                },
                isLoaded: false,
                isLoading: false,
                errors: null,
                data: null
            },
        }

        AssignObjectDataByMap(state, props.presetData || {}, PresetDataMap);
        this.state = state;

        this.setStats = this.setStats.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);

        this.doLoadJobsRunStats = this.doLoadJobsRunStats.bind(this);
        this.doLoadAccountsWithAgentsStats = this.doLoadAccountsWithAgentsStats.bind(this);
        this.doLoadActiveAccountsStats = this.doLoadActiveAccountsStats.bind(this);
        this.doLoadBackupsStats = this.doLoadBackupsStats.bind(this);
        this.doLoadUploadsStats = this.doLoadUploadsStats.bind(this);
        this.doLoadDownloadsStats = this.doLoadDownloadsStats.bind(this);
        this.doLoadRestoresStats = this.doLoadRestoresStats.bind(this);
        this.doLoadAgentsByVersionStats = this.doLoadAgentsByVersionStats.bind(this);
        this.doLoadAgentsByDestinationTypeStats = this.doLoadAgentsByDestinationTypeStats.bind(this);
        this.doLoadAgentsByServerTypeStats = this.doLoadAgentsByServerTypeStats.bind(this);
        this.doLoadAgentsByServicePlanStats = this.doLoadAgentsByServicePlanStats.bind(this);
        this.doLoadMailsStats = this.doLoadMailsStats.bind(this);
        this.doLoad = this.doLoad.bind(this);

        this.renderRequestDuration = this.renderRequestDuration.bind(this);
        this.renderJobsRunStats = this.renderJobsRunStats.bind(this);
        this.renderAccountsWithAgentsStats = this.renderAccountsWithAgentsStats.bind(this);
        this.renderActiveAccountsStats = this.renderActiveAccountsStats.bind(this);
        this.renderBackupsStats = this.renderBackupsStats.bind(this);
        this.renderUploadsStats = this.renderUploadsStats.bind(this);
        this.renderDownloadsStats = this.renderDownloadsStats.bind(this);
        this.renderRestoresStats = this.renderRestoresStats.bind(this);
        this.renderAgentsByVersionStats = this.renderAgentsByVersionStats.bind(this);
        this.renderAgentsByDestinationTypeStats = this.renderAgentsByDestinationTypeStats.bind(this);
        this.renderAgentsByServerTypeStats = this.renderAgentsByServerTypeStats.bind(this);
        this.renderAgentsByServicePlanStats = this.renderAgentsByServicePlanStats.bind(this);
        this.renderMailsStats = this.renderMailsStats.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    setStats(statsName, data, callback) {
        const self = this;

        const stats = {...self.state[statsName], ...data};

        self.setState({[statsName]: stats}, callback);
    }

    handleFilterChange(statsName, filterParamName, filterParamValue) {
        const self = this;

        const filter = {...self.state[statsName].filter, ...{[filterParamName]: filterParamValue}};

        self.setStats(statsName, {filter: filter});
    }

    doLoadJobsRunStats() {
        const self = this;

        if (!self._isMounted || self.state.jobsRunStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('jobsRunStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getJobsRunStats',
                            data: self.state.jobsRunStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const jobsRunStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};
                
                jobsRunStats.data.Last24Hours = NormalizeDataTable(responseData.Last24Hours);
                jobsRunStats.data.Last4Weeks = NormalizeDataTable(responseData.Last4Weeks);
                jobsRunStats.data.Last1Year = NormalizeDataTable(responseData.Last1Year);

                self.setStats('jobsRunStats', jobsRunStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('jobsRunStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoadAccountsWithAgentsStats() {
        const self = this;

        if (!self._isMounted || self.state.accountsWithAgentsStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('accountsWithAgentsStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAccountsWithAgentsStats',
                            data: self.state.accountsWithAgentsStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const accountsWithAgentsStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};
                
                accountsWithAgentsStats.data.Items = NormalizeDataTable(responseData.Items);

                self.setStats('accountsWithAgentsStats', accountsWithAgentsStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('accountsWithAgentsStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoadActiveAccountsStats() {
        const self = this;

        if (!self._isMounted || self.state.activeAccountsStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('activeAccountsStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getActiveAccountsStats',
                            data: self.state.activeAccountsStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const activeAccountsStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};
                
                activeAccountsStats.data.Items = NormalizeDataTable(responseData.Items);

                self.setStats('activeAccountsStats', activeAccountsStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('activeAccountsStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoadBackupsStats() {
        const self = this;

        if (!self._isMounted || self.state.backupsStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('backupsStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getBackupsStats',
                            data: self.state.backupsStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const backupsStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};
                
                backupsStats.data.Last24Hours = NormalizeDataTable(responseData.Last24Hours);
                backupsStats.data.Last4Weeks = NormalizeDataTable(responseData.Last4Weeks);
                backupsStats.data.Last1Year = NormalizeDataTable(responseData.Last1Year);

                self.setStats('backupsStats', backupsStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('backupsStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoadUploadsStats() {
        const self = this;

        if (!self._isMounted || self.state.uploadsStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('uploadsStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getUploadsStats',
                            data: self.state.uploadsStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const uploadsStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};
                
                uploadsStats.data.Last24Hours = NormalizeDataTable(responseData.Last24Hours);
                uploadsStats.data.Last4Weeks = NormalizeDataTable(responseData.Last4Weeks);
                uploadsStats.data.Last1Year = NormalizeDataTable(responseData.Last1Year);

                self.setStats('uploadsStats', uploadsStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('uploadsStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoadDownloadsStats() {
        const self = this;

        if (!self._isMounted || self.state.downloadsStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('downloadsStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getDownloadsStats',
                            data: self.state.downloadsStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const downloadsStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};
                
                downloadsStats.data.Last1Year = NormalizeDataTable(responseData.Last1Year);
                downloadsStats.data.Last24Hours = NormalizeDataTable(responseData.Last24Hours);
                downloadsStats.data.Last4Weeks = NormalizeDataTable(responseData.Last4Weeks);

                self.setStats('downloadsStats', downloadsStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('downloadsStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoadRestoresStats() {
        const self = this;

        if (!self._isMounted || self.state.restoresStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('restoresStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getRestoresStats',
                            data: self.state.restoresStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const restoresStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};

                restoresStats.data.Last1Year = NormalizeDataTable(responseData.Last1Year);
                restoresStats.data.Last24Hours = NormalizeDataTable(responseData.Last24Hours);
                restoresStats.data.Last4Weeks = NormalizeDataTable(responseData.Last4Weeks);

                self.setStats('restoresStats', restoresStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('restoresStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoadAgentsByVersionStats() {
        const self = this;

        if (!self._isMounted || self.state.agentsByVersionStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('agentsByVersionStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentsByVersionStats',
                            data: self.state.agentsByVersionStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const agentsByVersionStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};
                
                agentsByVersionStats.data.TableData = NormalizeDataTable(responseData.TableData);

                self.setStats('agentsByVersionStats', agentsByVersionStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('agentsByVersionStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoadAgentsByDestinationTypeStats() {
        const self = this;

        if (!self._isMounted || self.state.agentsByDestinationTypeStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('agentsByDestinationTypeStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentsByDestinationTypeStats',
                            data: self.state.agentsByDestinationTypeStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const agentsByDestinationTypeStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};
                
                agentsByDestinationTypeStats.data.Items = NormalizeDataTable(responseData.Items);

                self.setStats('agentsByDestinationTypeStats', agentsByDestinationTypeStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('agentsByDestinationTypeStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoadAgentsByServerTypeStats() {
        const self = this;

        if (!self._isMounted || self.state.agentsByServerTypeStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('agentsByServerTypeStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentsByServerTypeStats',
                            data: self.state.agentsByServerTypeStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const agentsByServerTypeStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};
                
                agentsByServerTypeStats.data.Items = NormalizeDataTable(responseData.Items);

                self.setStats('agentsByServerTypeStats', agentsByServerTypeStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('agentsByServerTypeStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoadAgentsByServicePlanStats() {
        const self = this;

        if (!self._isMounted || self.state.agentsByServicePlanStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('agentsByServicePlanStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentsByServicePlanStats',
                            data: self.state.agentsByServicePlanStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const agentsByServicePlanStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};
                
                agentsByServicePlanStats.data.Items = NormalizeDataTable(responseData.Items);

                self.setStats('agentsByServicePlanStats', agentsByServicePlanStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('agentsByServicePlanStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoadMailsStats() {
        const self = this;

        if (!self._isMounted || self.state.mailsStats.isLoading) {
            return;
        }

        new Promise(resolve => {
            self.setStats('mailsStats', {isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getMailsStats',
                            data: self.state.mailsStats.filter,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const mailsStats = {isLoaded: true, isLoading: false, errors: null, data: responseData};
                
                mailsStats.data.Last24Hours = NormalizeDataTable(responseData.Last24Hours);
                mailsStats.data.Last4Weeks = NormalizeDataTable(responseData.Last4Weeks);
                mailsStats.data.Last1Year = NormalizeDataTable(responseData.Last1Year);

                self.setStats('mailsStats', mailsStats);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setStats('mailsStats', {isLoading: false, errors: errors});
            }
        });
    }

    doLoad() {
        const self = this;

        self.doLoadJobsRunStats();
        self.doLoadAccountsWithAgentsStats();
        self.doLoadActiveAccountsStats();
        self.doLoadBackupsStats();
        self.doLoadUploadsStats();
        self.doLoadDownloadsStats();
        self.doLoadRestoresStats();
        self.doLoadAgentsByVersionStats();
        self.doLoadAgentsByDestinationTypeStats();
        self.doLoadAgentsByServerTypeStats();
        self.doLoadAgentsByServicePlanStats();
        self.doLoadMailsStats();
    }

    renderRequestDuration(statsData, noShiftTop) {
        const {timeZoneId} = this.props;
        const startedAt = moment.utc(statsData?.StartedAt ?? '');
        const finishedAt = moment.utc(statsData?.FinishedAt ?? '');

        if (startedAt.isValid()) {
            return (
                <div className={'fs-xs text-secondary' + (!Boolean(noShiftTop) ? ' mte-n3' : '')}>
                    Updated at {startedAt.tz(timeZoneId).format('L LT')}
                    {finishedAt.isValid() && 
                        <span>
                            &nbsp;(duration: <DurationComponent startedAt={startedAt} finishedAt={finishedAt} displayMilliseconds displayHours={false} displayMinutes={false} />)
                        </span>
                    }
                </div>
            )
        }
        else {
            return null;
        }
    }

    renderJobsRunStats() {
        const self = this;
        const {jobsRunStats} = self.state;
        const {jobTypes, applicationTypes} = self.props;

        let started24h = 0;
        let finished24h = 0;
        let failed24h = 0;

        let started4w = 0;
        let finished4w = 0;
        let failed4w = 0;

        let startedPerDay = 0;
        let finishedPerDay = 0;
        let failedPerDay = 0;

        if (jobsRunStats.isLoaded) {
            jobsRunStats.data.Last24Hours.rows.forEach(r => {
                started24h += r.c[1].v;
                finished24h += r.c[2].v + r.c[3].v;
                failed24h += r.c[3].v;
            });

            jobsRunStats.data.Last4Weeks.rows.forEach(r => {
                started4w += r.c[1].v;
                finished4w += r.c[2].v + r.c[3].v;
                failed4w += r.c[3].v;
            });

            if (jobsRunStats.data.Last1Year.rows.length > 0) {
                let started1y = 0;
                let finished1y = 0;
                let failed1y = 0;

                jobsRunStats.data.Last1Year.rows.forEach(r => {
                    started1y += r.c[1].v;
                    finished1y += r.c[2].v + r.c[3].v;
                    failed1y += r.c[3].v;
                });

                startedPerDay = started1y / jobsRunStats.data.Last1Year.rows.length;
                finishedPerDay = finished1y / jobsRunStats.data.Last1Year.rows.length;
                failedPerDay = failed1y / jobsRunStats.data.Last1Year.rows.length;
            }
        }

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="jobsRunStats.ApplicationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={jobsRunStats.isLoading}
                            value={ToString(jobsRunStats.filter.ApplicationTypeId)}
                            onChange={e => self.handleFilterChange('jobsRunStats', 'ApplicationTypeId', e.target.value)}
                        >
                            {applicationTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="jobsRunStats.ApplicationTypeId">Application type</label>
                    </div>
                    <div className="form-group newstyle">
                        <select
                            id="jobsRunStats.JobTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={jobsRunStats.isLoading}
                            value={ToString(jobsRunStats.filter.JobTypeId)}
                            onChange={e => self.handleFilterChange('jobsRunStats', 'JobTypeId', e.target.value)}
                        >
                            {jobTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="jobsRunStats.JobTypeId">Job type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={jobsRunStats.isLoading}
                        animated={jobsRunStats.isLoading}
                        onClick={self.doLoadJobsRunStats}
                    />
                </div>
                {jobsRunStats.isLoaded && self.renderRequestDuration(jobsRunStats.data)}
                <ErrorsBlockComponent errors={jobsRunStats.errors} unstyled />
                {jobsRunStats.isLoaded &&
                    <>
                        <div className="pt-3">
                            <h4>Last 24 hours (step 5 minutes)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total started: {numeral(started24h).format('0,0[.]0')}</div>
                                <div>Total finished: {numeral(finished24h).format('0,0[.]0')}</div>
                                {(failed24h > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed24h).format('0,0[.]0')} (${numeral(100 * failed24h / finished24h).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={jobsRunStats.data.Last24Hours} 
                                    options={JobRunsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Last 4 weeks (step 1 hour)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total started: {numeral(started4w).format('0,0[.]0')}</div>
                                <div>Total finished: {numeral(finished4w).format('0,0[.]0')}</div>
                                {(failed4w > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed4w).format('0,0[.]0')} (${numeral(100 * failed4w / finished4w).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={jobsRunStats.data.Last4Weeks} 
                                    options={JobRunsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Up to 1 year (step 1 day)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Started per day: {numeral(startedPerDay).format('0,0[.]0')}</div>
                                <div>Finished per day: {numeral(finishedPerDay).format('0,0[.]0')}</div>
                                {(failedPerDay > 0) &&
                                    <div className="text-danger">{`Failed per day: ${numeral(failedPerDay).format('0,0[.]0')} (${numeral(100 * failedPerDay / finishedPerDay).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={jobsRunStats.data.Last1Year} 
                                    options={JobRunsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                    </>
                }
            </>
        )
    }

    renderAccountsWithAgentsStats() {
        const self = this;
        const {accountsWithAgentsStats} = self.state;
        const {applicationTypes} = self.props;

        let averageCount = 0;

        if (accountsWithAgentsStats.isLoaded && (accountsWithAgentsStats.data.Items.rows.length > 0)) {
            const totalCount = accountsWithAgentsStats.data.Items.rows.reduce((a, r) => a + r.c[1].v, 0);
            averageCount = totalCount / accountsWithAgentsStats.data.Items.rows.length;
        }

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="accountsWithAgentsStats.ApplicationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={accountsWithAgentsStats.isLoading}
                            value={ToString(accountsWithAgentsStats.filter.ApplicationTypeId)}
                            onChange={e => self.handleFilterChange('accountsWithAgentsStats', 'ApplicationTypeId', e.target.value)}
                        >
                            {applicationTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="accountsWithAgentsStats.ApplicationTypeId">Application type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={accountsWithAgentsStats.isLoading}
                        animated={accountsWithAgentsStats.isLoading}
                        onClick={self.doLoadAccountsWithAgentsStats}
                    />
                </div>
                {accountsWithAgentsStats.isLoaded && self.renderRequestDuration(accountsWithAgentsStats.data)}
                <ErrorsBlockComponent errors={accountsWithAgentsStats.errors} unstyled />
                {accountsWithAgentsStats.isLoaded &&
                    <div className="pt-3">
                        <h4>Last year (step 1 day)</h4>
                        <div className="py-1">
                            Average count: {numeral(averageCount).format('0,0[.]0')}
                        </div>
                        <div className="pt-1 pb-2">
                            <SimpleLineChart 
                                dataTable={accountsWithAgentsStats.data.Items} 
                                options={DefaultChartOptions}
                            />
                        </div>
                    </div>
                }
            </>
        )
    }

    renderActiveAccountsStats() {
        const self = this;
        const {activeAccountsStats} = self.state;
        const {applicationTypes} = self.props;

        let averageCount = 0;

        if (activeAccountsStats.isLoaded && (activeAccountsStats.data.Items.rows.length > 0)) {
            const totalCount = activeAccountsStats.data.Items.rows.reduce((a, r) => a + r.c[1].v, 0);
            averageCount = totalCount / activeAccountsStats.data.Items.rows.length;
        }

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="activeAccountsStats.ApplicationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={activeAccountsStats.isLoading}
                            value={ToString(activeAccountsStats.filter.ApplicationTypeId)}
                            onChange={e => self.handleFilterChange('activeAccountsStats', 'ApplicationTypeId', e.target.value)}
                        >
                            {applicationTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="activeAccountsStats.ApplicationTypeId">Application type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={activeAccountsStats.isLoading}
                        animated={activeAccountsStats.isLoading}
                        onClick={self.doLoadActiveAccountsStats}
                    />
                </div>
                {activeAccountsStats.isLoaded && self.renderRequestDuration(activeAccountsStats.data)}
                <div className="fs-sm text-secondary">An account is considered active on some date if there was at least one job run for the previous week.</div>
                <ErrorsBlockComponent errors={activeAccountsStats.errors} unstyled />
                {activeAccountsStats.isLoaded &&
                    <div className="pt-3">
                        <h4>Up to 1 year (step 1 day)</h4>
                        <div className="py-1">
                            Average count: {numeral(averageCount).format('0,0[.]0')}
                        </div>
                        <div className="pt-1 pb-2">
                            <SimpleLineChart 
                                dataTable={activeAccountsStats.data.Items} 
                                options={DefaultChartOptions}
                            />
                        </div>
                    </div>
                }
            </>
        )
    }

    renderBackupsStats() {
        const self = this;
        const {backupsStats} = self.state;
        const {applicationTypes, backupTypes, serverTypes} = self.props;

        let count24h = 0;
        let failed24h = 0;

        let count4w = 0;
        let failed4w = 0;

        let countPerDay = 0;
        let failedPerDay = 0;

        if (backupsStats.isLoaded) {
            backupsStats.data.Last24Hours.rows.forEach(r => {
                count24h += r.c[1].v;
                failed24h += r.c[2].v;
            });

            backupsStats.data.Last4Weeks.rows.forEach(r => {
                count4w += r.c[1].v;
                failed4w += r.c[2].v;
            });

            if (backupsStats.data.Last1Year.rows.length > 0) {
                let count = 0;
                let failed = 0;

                backupsStats.data.Last1Year.rows.forEach(r => {
                    count += r.c[1].v;
                    failed += r.c[2].v;
                })

                countPerDay = count / backupsStats.data.Last1Year.rows.length;
                failedPerDay = failed / backupsStats.data.Last1Year.rows.length;
            }
        }

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="backupsStats.ApplicationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={backupsStats.isLoading}
                            value={ToString(backupsStats.filter.ApplicationTypeId)}
                            onChange={e => self.handleFilterChange('backupsStats', 'ApplicationTypeId', e.target.value)}
                        >
                            {applicationTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="backupsStats.ApplicationTypeId">Application type</label>
                    </div>
                    <div className="form-group newstyle">
                        <select
                            id="backupsStats.BackupTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={backupsStats.isLoading}
                            value={ToString(backupsStats.filter.BackupTypeId)}
                            onChange={e => self.handleFilterChange('backupsStats', 'BackupTypeId', e.target.value)}
                        >
                            {backupTypes
                                .map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="backupsStats.BackupTypeId">Backup type</label>
                    </div>
                    <div className="form-group newstyle">
                        <select
                            id="backupsStats.ServerTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={backupsStats.isLoading}
                            value={ToString(backupsStats.filter.ServerTypeId)}
                            onChange={e => self.handleFilterChange('backupsStats', 'ServerTypeId', e.target.value)}
                        >
                            {serverTypes
                                .map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="backupsStats.ServerTypeId">DBMS type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={backupsStats.isLoading}
                        animated={backupsStats.isLoading}
                        onClick={self.doLoadBackupsStats}
                    />
                </div>
                {backupsStats.isLoaded && self.renderRequestDuration(backupsStats.data)}
                <ErrorsBlockComponent errors={backupsStats.errors} unstyled />
                {backupsStats.isLoaded &&
                    <>
                        <div className="pt-3">
                            <h4>Last 24 hours (step 5 minutes)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total backups: {numeral(count24h).format('0,0[.]0')}</div>
                                {(failed24h > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed24h).format('0,0[.]0')} (${numeral(100 * failed24h / count24h).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={backupsStats.data.Last24Hours} 
                                    options={BackupsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Last 4 weeks (step 1 hour)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total backups: {numeral(count4w).format('0,0[.]0')}</div>
                                {(failed4w > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed4w).format('0,0[.]0')} (${numeral(100 * failed4w / count4w).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={backupsStats.data.Last4Weeks} 
                                    options={BackupsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Up to 1 year (step 1 day)</h4>
                            <div className="py-1">
                                <div>Backups per day: {numeral(countPerDay).format('0,0[.]0')}</div>
                                {(failedPerDay > 0) &&
                                    <div className="text-danger">{`Failed per day: ${numeral(failedPerDay).format('0,0[.]0')} (${numeral(100 * failedPerDay / countPerDay).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={backupsStats.data.Last1Year} 
                                    options={BackupsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                    </>
                }
            </>
        )
    }

    renderUploadsStats() {
        const self = this;
        const {uploadsStats} = self.state;
        const {applicationTypes, destinationTypes} = self.props;

        let count24h = 0;
        let failed24h = 0;

        let count4w = 0;
        let failed4w = 0;

        let countPerDay = 0;
        let failedPerDay = 0;

        if (uploadsStats.isLoaded) {
            uploadsStats.data.Last24Hours.rows.forEach(r => {
                count24h += r.c[1].v;
                failed24h += r.c[2].v;
            });

            uploadsStats.data.Last4Weeks.rows.forEach(r => {
                count4w += r.c[1].v;
                failed4w += r.c[2].v;
            });

            if (uploadsStats.data.Last1Year.rows.length > 0) {
                let count = 0;
                let failed = 0;

                uploadsStats.data.Last1Year.rows.forEach(r => {
                    count += r.c[1].v;
                    failed += r.c[2].v;
                })

                countPerDay = count / uploadsStats.data.Last1Year.rows.length;
                failedPerDay = failed / uploadsStats.data.Last1Year.rows.length;
            }
        }

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="uploadsStats.ApplicationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={uploadsStats.isLoading}
                            value={ToString(uploadsStats.filter.ApplicationTypeId)}
                            onChange={e => self.handleFilterChange('uploadsStats', 'ApplicationTypeId', e.target.value)}
                        >
                            {applicationTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="uploadsStats.ApplicationTypeId">Application type</label>
                    </div>
                    <div className="form-group newstyle">
                        <select
                            id="uploadsStats.DestinationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={uploadsStats.isLoading}
                            value={ToString(uploadsStats.filter.DestinationTypeId)}
                            onChange={e => self.handleFilterChange('uploadsStats', 'DestinationTypeId', e.target.value)}
                        >
                            {destinationTypes
                                .map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="uploadsStats.DestinationTypeId">Destination type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={uploadsStats.isLoading}
                        animated={uploadsStats.isLoading}
                        onClick={self.doLoadUploadsStats}
                    />
                </div>
                {uploadsStats.isLoaded && self.renderRequestDuration(uploadsStats.data)}
                <ErrorsBlockComponent errors={uploadsStats.errors} unstyled />
                {uploadsStats.isLoaded &&
                    <>
                        <div className="pt-3">
                            <h4>Last 24 hours (step 5 minutes)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total uploads: {numeral(count24h).format('0,0[.]0')}</div>
                                {(failed24h > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed24h).format('0,0[.]0')} (${numeral(100 * failed24h / count24h).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={uploadsStats.data.Last24Hours} 
                                    options={UploadsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Last 4 weeks (step 1 hour)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total uploads: {numeral(count4w).format('0,0[.]0')}</div>
                                {(failed4w > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed4w).format('0,0[.]0')} (${numeral(100 * failed4w / count4w).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={uploadsStats.data.Last4Weeks} 
                                    options={UploadsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Up to 1 year (step 1 day)</h4>
                            <div className="py-1">
                                <div>Uploads per day: {numeral(countPerDay).format('0,0[.]0')}</div>
                                {(failedPerDay > 0) &&
                                    <div className="text-danger">{`Failed per day: ${numeral(failedPerDay).format('0,0[.]0')} (${numeral(100 * failedPerDay / countPerDay).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={uploadsStats.data.Last1Year} 
                                    options={UploadsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                    </>
                }
            </>
        )
    }

    renderDownloadsStats() {
        const self = this;
        const {downloadsStats} = self.state;
        const {applicationTypes, destinationTypes} = self.props;

        let started24h = 0;
        let finished24h = 0;
        let failed24h = 0;

        let started4w = 0;
        let finished4w = 0;
        let failed4w = 0;

        let startedPerDay = 0;
        let finishedPerDay = 0;
        let failedPerDay = 0;

        if (downloadsStats.isLoaded) {
            downloadsStats.data.Last24Hours.rows.forEach(r => {
                started24h += r.c[1].v;
                finished24h += r.c[2].v + r.c[3].v;
                failed24h += r.c[3].v;
            });

            downloadsStats.data.Last4Weeks.rows.forEach(r => {
                started4w += r.c[1].v;
                finished4w += r.c[2].v + r.c[3].v;
                failed4w += r.c[3].v;
            });

            if (downloadsStats.data.Last1Year.rows.length > 0) {
                let started1y = 0;
                let finished1y = 0;
                let failed1y = 0;

                downloadsStats.data.Last1Year.rows.forEach(r => {
                    started1y += r.c[1].v;
                    finished1y += r.c[2].v + r.c[3].v;
                    failed1y += r.c[3].v;
                });

                startedPerDay = started1y / downloadsStats.data.Last1Year.rows.length;
                finishedPerDay = finished1y / downloadsStats.data.Last1Year.rows.length;
                failedPerDay = failed1y / downloadsStats.data.Last1Year.rows.length;
            }
        }

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="downloadsStats.ApplicationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={downloadsStats.isLoading}
                            value={ToString(downloadsStats.filter.ApplicationTypeId)}
                            onChange={e => self.handleFilterChange('downloadsStats', 'ApplicationTypeId', e.target.value)}
                        >
                            {applicationTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="downloadsStats.ApplicationTypeId">Application type</label>
                    </div>
                    <div className="form-group newstyle">
                        <select
                            id="downloadsStats.DestinationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={downloadsStats.isLoading}
                            value={ToString(downloadsStats.filter.DestinationTypeId)}
                            onChange={e => self.handleFilterChange('downloadsStats', 'DestinationTypeId', e.target.value)}
                        >
                            {destinationTypes
                                .map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="downloadsStats.DestinationTypeId">Destination type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={downloadsStats.isLoading}
                        animated={downloadsStats.isLoading}
                        onClick={self.doLoadDownloadsStats}
                    />
                </div>
                {downloadsStats.isLoaded && self.renderRequestDuration(downloadsStats.data)}
                <ErrorsBlockComponent errors={downloadsStats.errors} unstyled />
                {downloadsStats.isLoaded &&
                    <>
                        <div className="pt-3">
                            <h4>Last 24 hours (step 5 minutes)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total started: {numeral(started24h).format('0,0[.]0')}</div>
                                <div>Total finished: {numeral(finished24h).format('0,0[.]0')}</div>
                                {(failed24h > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed24h).format('0,0[.]0')} (${numeral(100 * failed24h / finished24h).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={downloadsStats.data.Last24Hours} 
                                    options={DownloadsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Last 4 weeks (step 1 hour)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total started: {numeral(started4w).format('0,0[.]0')}</div>
                                <div>Total finished: {numeral(finished4w).format('0,0[.]0')}</div>
                                {(failed4w > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed4w).format('0,0[.]0')} (${numeral(100 * failed4w / finished4w).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={downloadsStats.data.Last4Weeks} 
                                    options={DownloadsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Up to 1 year (step 1 day)</h4>
                            <div className="py-1">
                                <div>Started per day: {numeral(startedPerDay).format('0,0[.]0')}</div>
                                <div>Finished per day: {numeral(finishedPerDay).format('0,0[.]0')}</div>
                                {(failedPerDay > 0) &&
                                    <div className="text-danger">Failed per day: {`${numeral(failedPerDay).format('0,0[.]0')} (${numeral(100 * failedPerDay / finishedPerDay).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={downloadsStats.data.Last1Year} 
                                    options={DownloadsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                    </>
                }
            </>
        )
    }

    renderRestoresStats() {
        const self = this;
        const {restoresStats} = self.state;
        const {applicationTypes, serverTypes} = self.props;

        let started24h = 0;
        let finished24h = 0;
        let failed24h = 0;

        let started4w = 0;
        let finished4w = 0;
        let failed4w = 0;

        let startedPerDay = 0;
        let finishedPerDay = 0;
        let failedPerDay = 0;

        if (restoresStats.isLoaded) {
            restoresStats.data.Last24Hours.rows.forEach(r => {
                started24h += r.c[1].v;
                finished24h += r.c[2].v + r.c[3].v;
                failed24h += r.c[3].v;
            });

            restoresStats.data.Last4Weeks.rows.forEach(r => {
                started4w += r.c[1].v;
                finished4w += r.c[2].v + r.c[3].v;
                failed4w += r.c[3].v;
            });

            if (restoresStats.data.Last1Year.rows.length > 0) {
                let started1y = 0;
                let finished1y = 0;
                let failed1y = 0;

                restoresStats.data.Last1Year.rows.forEach(r => {
                    started1y += r.c[1].v;
                    finished1y += r.c[2].v + r.c[3].v;
                    failed1y += r.c[3].v;
                });

                startedPerDay = started1y / restoresStats.data.Last1Year.rows.length;
                finishedPerDay = finished1y / restoresStats.data.Last1Year.rows.length;
                failedPerDay = failed1y / restoresStats.data.Last1Year.rows.length;
            }
        }

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="restoresStats.ApplicationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={restoresStats.isLoading}
                            value={ToString(restoresStats.filter.ApplicationTypeId)}
                            onChange={e => self.handleFilterChange('restoresStats', 'ApplicationTypeId', e.target.value)}
                        >
                            {applicationTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="restoresStats.ApplicationTypeId">Application type</label>
                    </div>
                    <div className="form-group newstyle">
                        <select
                            id="restoresStats.ServerTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={restoresStats.isLoading}
                            value={ToString(restoresStats.filter.ServerTypeId)}
                            onChange={e => self.handleFilterChange('restoresStats', 'ServerTypeId', e.target.value)}
                        >
                            {serverTypes
                                .map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="restoresStats.ServerTypeId">DBMS type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={restoresStats.isLoading}
                        animated={restoresStats.isLoading}
                        onClick={self.doLoadRestoresStats}
                    />
                </div>
                {restoresStats.isLoaded && self.renderRequestDuration(restoresStats.data)}
                <ErrorsBlockComponent errors={restoresStats.errors} unstyled />
                {restoresStats.isLoaded &&
                    <>
                        <div className="pt-3">
                            <h4>Last 24 hours (step 5 minutes)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total started: {numeral(started24h).format('0,0[.]0')}</div>
                                <div>Total finished: {numeral(finished24h).format('0,0[.]0')}</div>
                                {(failed24h > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed24h).format('0,0[.]0')} (${numeral(100 * failed24h / finished24h).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={restoresStats.data.Last24Hours} 
                                    options={RestoresChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Last 4 weeks (step 1 hour)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total started: {numeral(started4w).format('0,0[.]0')}</div>
                                <div>Total finished: {numeral(finished4w).format('0,0[.]0')}</div>
                                {(failed4w > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed4w).format('0,0[.]0')} (${numeral(100 * failed4w / finished4w).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={restoresStats.data.Last4Weeks} 
                                    options={RestoresChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Up to 1 year (step 1 day)</h4>
                            <div className="py-1">
                                <div>Started per day: {numeral(startedPerDay).format('0,0[.]0')}</div>
                                <div>Finished per day: {numeral(finishedPerDay).format('0,0[.]0')}</div>
                                {(failedPerDay > 0) &&
                                    <div className="text-danger">Failed per day: {`${numeral(failedPerDay).format('0,0[.]0')} (${numeral(100 * failedPerDay / finishedPerDay).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={restoresStats.data.Last1Year} 
                                    options={RestoresChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                    </>
                }
            </>
        )
    }

    renderAgentsByVersionStats() {
        const self = this;
        const {agentsByVersionStats} = self.state;
        const {applicationTypes, timeZoneId} = self.props;

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="agentsByVersionStats.ApplicationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={agentsByVersionStats.isLoading}
                            value={ToString(agentsByVersionStats.filter.ApplicationTypeId)}
                            onChange={e => self.handleFilterChange('agentsByVersionStats', 'ApplicationTypeId', e.target.value)}
                        >
                            {applicationTypes
                                .filter(p => ToString(p.Key).length > 0)
                                .map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="agentsByVersionStats.ApplicationTypeId">Application type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={agentsByVersionStats.isLoading}
                        animated={agentsByVersionStats.isLoading}
                        onClick={self.doLoadAgentsByVersionStats}
                    />
                </div>
                <div>
                    <Checkbox 
                        id="agentsByVersionStats.HideInactive"
                        checked={agentsByVersionStats.filter.HideInactive}
                        onChange={e => self.handleFilterChange('agentsByVersionStats', 'HideInactive', e.target.checked)}
                        label="Hide inactive"
                    />
                </div>
                {agentsByVersionStats.isLoaded && self.renderRequestDuration(agentsByVersionStats.data, true)}
                <div className="fs-sm text-secondary">An agent is considered active if there was at least one job run or restore run for the previous week.</div>
                <ErrorsBlockComponent errors={agentsByVersionStats.errors} unstyled />
                {agentsByVersionStats.isLoaded &&
                    <div className="pt-3">
                        <div className="d-inline-flex">
                            <SimpleTable 
                                className="table-striped-even d-block table-hover newstyle fs-sm w-auto"
                                timeZoneId ={timeZoneId}
                                tableData={agentsByVersionStats.data.TableData} 
                                style={{maxHeight: '300px', overflowY: 'auto'}}
                                rowsFilter={agentsByVersionStats.filter.HideInactive ? r => ((r.p.f1 == 'ta') || (r.p.f1 == 'ra')) : r => r.p.f1 != 'ta'}
                            />
                        </div>
                    </div>
                }
            </>
        )
    }

    renderAgentsByDestinationTypeStats() {
        const self = this;
        const {agentsByDestinationTypeStats} = self.state;
        const {applicationTypes} = self.props;

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="agentsByDestinationTypeStats.ApplicationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={agentsByDestinationTypeStats.isLoading}
                            value={ToString(agentsByDestinationTypeStats.filter.ApplicationTypeId)}
                            onChange={e => self.handleFilterChange('agentsByDestinationTypeStats', 'ApplicationTypeId', e.target.value)}
                        >
                            {applicationTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="agentsByDestinationTypeStats.ApplicationTypeId">Application type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={agentsByDestinationTypeStats.isLoading}
                        animated={agentsByDestinationTypeStats.isLoading}
                        onClick={self.doLoadAgentsByDestinationTypeStats}
                    />
                </div>
                {agentsByDestinationTypeStats.isLoaded && self.renderRequestDuration(agentsByDestinationTypeStats.data)}
                <ErrorsBlockComponent errors={agentsByDestinationTypeStats.errors} unstyled />
                {agentsByDestinationTypeStats.isLoaded &&
                    <div className="pt-3">
                        <h4>Up to 1 year (step 1 day)</h4>
                        <div className="pt-1 pb-2">
                            <SimpleLineChart 
                                dataTable={agentsByDestinationTypeStats.data.Items} 
                                options={AgentsChartOptions}
                                toggleColumnVisibleOnLegendClick
                            />
                        </div>
                    </div>
                }
            </>
        )
    }

    renderAgentsByServerTypeStats() {
        const self = this;
        const {agentsByServerTypeStats} = self.state;
        const {applicationTypes} = self.props;

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="agentsByServerTypeStats.ApplicationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={agentsByServerTypeStats.isLoading}
                            value={ToString(agentsByServerTypeStats.filter.ApplicationTypeId)}
                            onChange={e => self.handleFilterChange('agentsByServerTypeStats', 'ApplicationTypeId', e.target.value)}
                        >
                            {applicationTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="agentsByServerTypeStats.ApplicationTypeId">Application type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={agentsByServerTypeStats.isLoading}
                        animated={agentsByServerTypeStats.isLoading}
                        onClick={self.doLoadAgentsByServerTypeStats}
                    />
                </div>
                {agentsByServerTypeStats.isLoaded && self.renderRequestDuration(agentsByServerTypeStats.data)}
                <ErrorsBlockComponent errors={agentsByServerTypeStats.errors} unstyled />
                {agentsByServerTypeStats.isLoaded &&
                    <div className="pt-3">
                        <h4>Up to 1 year (step 1 day)</h4>
                        <div className="pt-1 pb-2">
                            <SimpleLineChart 
                                dataTable={agentsByServerTypeStats.data.Items} 
                                options={AgentsChartOptions}
                                toggleColumnVisibleOnLegendClick
                            />
                        </div>
                    </div>
                }
            </>
        )
    }

    renderAgentsByServicePlanStats() {
        const self = this;
        const {agentsByServicePlanStats} = self.state;
        const {applicationTypes} = self.props;

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="agentsByServicePlanStats.ApplicationTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={agentsByServicePlanStats.isLoading}
                            value={ToString(agentsByServicePlanStats.filter.ApplicationTypeId)}
                            onChange={e => self.handleFilterChange('agentsByServicePlanStats', 'ApplicationTypeId', e.target.value)}
                        >
                            {applicationTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="agentsByServicePlanStats.ApplicationTypeId">Application type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={agentsByServicePlanStats.isLoading}
                        animated={agentsByServicePlanStats.isLoading}
                        onClick={self.doLoadAgentsByServicePlanStats}
                    />
                </div>
                {agentsByServicePlanStats.isLoaded && self.renderRequestDuration(agentsByServicePlanStats.data)}
                <div className="fs-sm text-secondary">
                    For Free only active agents are counted.<br/>
                    An agent is considered active if there was at least one job run or restore run for the previous week.
                </div>
                <ErrorsBlockComponent errors={agentsByServicePlanStats.errors} unstyled />
                {agentsByServicePlanStats.isLoaded &&
                    <div className="pt-3">
                        <h4>Up to 1 year (step 1 day)</h4>
                        <div className="pt-1 pb-2">
                            <SimpleLineChart 
                                dataTable={agentsByServicePlanStats.data.Items} 
                                options={AgentsChartOptions}
                                invisibleColumns={agentsByServicePlanStats.invisibleColumns}
                                toggleColumnVisibleOnLegendClick
                                onInvisibleColumnsChange={cols => self.setStats('agentsByServicePlanStats', {invisibleColumns: cols})}
                            />
                        </div>
                    </div>
                }
            </>
        )
    }

    renderMailsStats() {
        const self = this;
        const {mailsStats} = self.state;
        const {mailTypes} = self.props;

        let sent24h = 0;
        let failed24h = 0;

        let sent4w = 0;
        let failed4w = 0;

        let sentPerDay = 0;
        let failedPerDay = 0;

        if (mailsStats.isLoaded) {
            mailsStats.data.Last24Hours.rows.forEach(r => {
                sent24h += r.c[1].v;
                failed24h += r.c[2].v;
            });

            mailsStats.data.Last4Weeks.rows.forEach(r => {
                sent4w += r.c[1].v;
                failed4w += r.c[2].v;
            });

            if (mailsStats.data.Last1Year.rows.length > 0) {
                let sent1y = 0;
                let failed1y = 0;

                mailsStats.data.Last1Year.rows.forEach(r => {
                    sent1y += r.c[1].v;
                    failed1y += r.c[2].v;
                });

                sentPerDay = sent1y / mailsStats.data.Last1Year.rows.length;
                failedPerDay = failed1y / mailsStats.data.Last1Year.rows.length;
            }
        }

        return (
            <>
                <div className="d-flex align-items-center">
                    <div className="form-group newstyle">
                        <select
                            id="mailsStats.MailTypeId"
                            className="custom-select mr-2 w-auto"
                            disabled={mailsStats.isLoading}
                            value={ToString(mailsStats.filter.MailTypeId)}
                            onChange={e => self.handleFilterChange('mailsStats', 'MailTypeId', e.target.value)}
                        >
                            {mailTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                        </select>
                        <label htmlFor="mailsStats.MailTypeId">Mail type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={mailsStats.isLoading}
                        animated={mailsStats.isLoading}
                        onClick={self.doLoadMailsStats}
                    />
                </div>
                {mailsStats.isLoaded && self.renderRequestDuration(mailsStats.data)}
                <ErrorsBlockComponent errors={mailsStats.errors} unstyled />
                {mailsStats.isLoaded &&
                    <>
                        <div className="pt-3">
                            <h4>Last 24 hours (step 5 minutes)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total sent: {numeral(sent24h).format('0,0[.]0')}</div>
                                {(failed24h > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed24h).format('0,0[.]0')} (${numeral(100 * failed24h / sent24h).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={mailsStats.data.Last24Hours} 
                                    options={MailsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Last 4 weeks (step 1 hour)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Total sent: {numeral(sent4w).format('0,0[.]0')}</div>
                                {(failed4w > 0) && 
                                    <div className="text-danger">Total failed: {`${numeral(failed4w).format('0,0[.]0')} (${numeral(100 * failed4w / sent4w).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={mailsStats.data.Last4Weeks} 
                                    options={MailsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                        <div className="pt-3">
                            <h4>Up to 1 year (step 1 day)</h4>
                            <div className="py-1 fs-sm text-secondary">
                                <div>Sent per day: {numeral(sentPerDay).format('0,0[.]00')}</div>
                                {(failedPerDay > 0) &&
                                    <div className="text-danger">{`Failed per day: ${numeral(failedPerDay).format('0,0[.]00')} (${numeral(100 * failedPerDay / sentPerDay).format('0,0[.]00')}%)`}</div>
                                }
                            </div>
                            <div className="pt-1 pb-2">
                                <SimpleLineChart 
                                    dataTable={mailsStats.data.Last1Year} 
                                    options={MailsChartOptions}
                                    toggleColumnVisibleOnLegendClick
                                />
                            </div>
                        </div>
                    </>
                }
            </>
        )
    }

    render() {
        const self = this;
        const {displayOrders} = self.props;

        const allChartsData = Object.keys(self.state)
            .filter(p => p.endsWith('Stats') && ObjectHasProperty(self.state, p))
            .map(p => self.state[p]);

        const isAnyLoading = allChartsData.some(p => p.isLoading);
        const isAllLoading = !allChartsData.some(p => !p.isLoading);

        const allSections = [
            {id: 'jobsRunStats', caption: 'Job runs', render: self.renderJobsRunStats},
            {id: 'accountsWithAgentsStats', caption: 'Accounts with at least one agent', render: self.renderAccountsWithAgentsStats},
            {id: 'activeAccountsStats', caption: 'Active accounts', render: self.renderActiveAccountsStats},
            {id: 'backupsStats', caption: 'Backups', render: self.renderBackupsStats},
            {id: 'uploadsStats', caption: 'Uploads', render: self.renderUploadsStats},
            {id: 'downloadsStats', caption: 'Downloads', render: self.renderDownloadsStats},
            {id: 'restoresStats', caption: 'Restores', render: self.renderRestoresStats},
            {id: 'agentsByVersionStats', caption: 'Agents by version', render: self.renderAgentsByVersionStats},
            {id: 'agentsByDestinationTypeStats', caption: 'Agents used destination type', render: self.renderAgentsByDestinationTypeStats},
            {id: 'agentsByServerTypeStats', caption: 'Agents used DBMS type', render: self.renderAgentsByServerTypeStats},
            {id: 'agentsByServicePlanStats', caption: 'Agents by service plan', render: self.renderAgentsByServicePlanStats},
            {id: 'mailsStats', caption: 'Mails', render: self.renderMailsStats},
        ];

        const sections = [];

        (displayOrders || [])
            .concat(DefaultDisplayOrders)
            .forEach(p => {
                const section = allSections.find(s => s.id == p);

                if (Boolean(section) && (sections.indexOf(section) < 0)) {
                    sections.push(section);
                }
            });

        allSections.forEach(p => {
            if (sections.indexOf(p) < 0) {
                sections.push(p);
            }
        });

        const toggleItemOpened = item => {
            const isOpened = Boolean(self.state[item.id].isOpened);

            self.setStats(item.id, {isOpened: !isOpened});
        }

        const renderItem = item => {
            const itemData = self.state[item.id];
            const isOpened = Boolean(itemData.isOpened);
            const isLoading = Boolean(itemData.isLoading);
            const hasErrors = !isLoading && !IsErrorsEmpty(itemData.errors);
            let content = null;

            if (isOpened) {
                content = item.render.call(self);
            }

            return (
                <div key={item.id} className="stats-item">
                    <div 
                        className={'stats-item-header btn-collapse' + (isOpened ? ' active' : '') + (hasErrors ? ' text-danger' : '' )} 
                        aria-expanded={isOpened}
                        onClick={() => toggleItemOpened(item)}
                    >
                        {item.caption}
                        {!isOpened && isLoading && <i className="spinner-border spinner-border-fs ml-2" />}
                    </div>
                    {isOpened && 
                        <div className="stats-item-content">
                            {item.render.call(self)}
                        </div>
                    }
                </div>
            )
        }

        return (
            <div>
                <PageHeader
                    fixed
                    captionTruncate
                    notWrappedMaxHeight={null}
                    caption={
                        <h2 className="text-truncate">
                            <span className="mre-2">Admin Dashboard</span>
                            <RefreshButtonComponent disabled={isAllLoading} animated={isAnyLoading} onClick={self.doLoad} />
                        </h2>
                    }
                />
                <div>
                    {sections.map(renderItem)}
                </div>
            </div>
        )
    }
}
AdminDashboardComponent.propTypes = {
    timeZoneId: PropTypes.string.isRequired,
    applicationTypes: PropTypes.arrayOf(PropTypes.shape({
        Key: PropTypes.string.isRequired,
        Value: PropTypes.string.isRequired
    })).isRequired,
    backupTypes: PropTypes.arrayOf(PropTypes.shape({
        Key: PropTypes.string.isRequired,
        Value: PropTypes.string.isRequired
    })).isRequired,
    serverTypes: PropTypes.arrayOf(PropTypes.shape({
        Key: PropTypes.string.isRequired,
        Value: PropTypes.string.isRequired
    })).isRequired,
    destinationTypes: PropTypes.arrayOf(PropTypes.shape({
        Key: PropTypes.string.isRequired,
        Value: PropTypes.string.isRequired
    })).isRequired,
    mailTypes: PropTypes.arrayOf(PropTypes.shape({
        Key: PropTypes.string.isRequired,
        Value: PropTypes.string.isRequired
    })).isRequired,
    displayOrders: PropTypes.arrayOf(PropTypes.string),
    presetData: PropTypes.object,
}
AdminDashboardComponent.defaultProps = {
}
